import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ModbusApi } from "~/api/modbus-api";
import { ComboBox } from "~/components/common/combo-box";
import { ContentContainer } from "~/components/common/content-container";
import { SimpleTable } from "~/components/common/simple-table";
import { Button, Input, Label } from "~/components/ui";
import type {
  ModbusByteOrder,
  ModbusMaster,
  ModbusRegisterDto,
  ModbusRegisterType,
  ModbusValueType,
} from "~/types/api";
import type { FormOption, SensorOptionType } from "~/types/ui";
import {
  deafaultModbusRegister,
  modbusByteOrders,
  ModbusRegisterActionEnum,
  modbusRegisterAllTypes,
  modbusRegisterWriteTypes,
  modbusValueTypes,
  registerCountByValueType,
  canUseRegister,
} from "~/utils/modbus-util";
import { defaultTableColumns } from "~/utils/table-util";

export default function ModbusFormField({
  value,
  onChange,
  options = [],
}: {
  value: ModbusRegisterDto[];
  onChange: (val: ModbusRegisterDto[]) => void;
  options?: {
    label: string;
    value: ModbusMaster[] | SensorOptionType[] | boolean;
  }[];
}) {
  const { t } = useTranslation();
  const [selectedRegister, setSelectedRegister] = useState<ModbusRegisterDto>(
    deafaultModbusRegister,
  );

  async function handleAdd(modbusRegister: ModbusRegisterDto) {
    const idx = value.findIndex(
      (x) => x.modbusRegisterId == modbusRegister.modbusRegisterId,
    );
    const nextRegister =
      modbusRegister.modbusRegisterId != undefined && idx != -1
        ? modbusRegister
        : { ...modbusRegister, modbusRegisterId: crypto.randomUUID() };

    setSelectedRegister(deafaultModbusRegister);
    onChange(
      idx != -1 ? value.with(idx, nextRegister) : value.concat(nextRegister),
    );
  }

  async function handleEdit(modbusRegisters: ModbusRegisterDto[]) {
    if (modbusRegisters.length > 1) {
      toast.error(
        t("common.errors.expectedSingleEditData", {
          resource: t("modbus.register.title"),
        }),
      );
      console.error(
        "Error: Can only edit 1 Modbus Register at a time. Will edit the first one",
      );
    }
    toast.success(
      t("common.editResourceAddSection", {
        resource: modbusRegisters.at(0)?.key ?? t("modbus.register.title"),
      }),
    );
    setSelectedRegister(modbusRegisters.at(0) ?? deafaultModbusRegister);
  }

  async function handleRemove(modbusRegisters: ModbusRegisterDto[]) {
    const removingKeys = modbusRegisters.map((x) => x.modbusRegisterId);
    const modifiedValue = value.filter(
      (x) => !removingKeys.includes(x.modbusRegisterId),
    );
    onChange(modifiedValue);
    setSelectedRegister(deafaultModbusRegister);
  }

  const colDef: ColumnDef<any>[] = [
    defaultTableColumns["select"],
    {
      id: "modbusMasterId",
      accessorKey: "modbusMasterId",
      header: t("modbus.register.fields.master"),
      cell: (info) => info.row.original?.name ?? info.getValue(),
    },
    {
      id: "slave",
      accessorKey: "slave",
      header: t("modbus.register.fields.slave"),
      cell: (info) => info.getValue(),
    },
    {
      id: "key",
      accessorKey: "key",
      header: t("modbus.register.fields.key"),
      cell: (info) => info.getValue(),
    },
    {
      id: "register",
      accessorKey: "register",
      header: t("modbus.register.fields.register"),
      cell: (info) => {
        const valueType = info.row.original?.valueType;
        return registerCountByValueType(valueType) > 1
          ? `${info.getValue()} - ${(info.getValue() as number) + registerCountByValueType(valueType) - 1}`
          : info.getValue();
      },
    },
    {
      id: "pollInterval",
      accessorKey: "pollInterval",
      header: t("modbus.register.fields.pollInterval"),
      cell: (info) => info.getValue(),
    },
    {
      id: "valueType",
      accessorKey: "valueType",
      header: t("modbus.register.fields.valueType"),
      cell: (info) => t(`modbus.register.valueTypes.${info.getValue()}`),
    },
    {
      id: "byteOrder",
      accessorKey: "byteOrder",
      header: t("modbus.register.fields.byteOrder"),
      cell: (info) => t(`modbus.register.byteOrder.${info.getValue()}`),
    },
    {
      id: "polledValue",
      accessorKey: "polledValue",
      header: t("modbus.register.fields.polledValue"),
      cell: (info) => info.getValue(),
    },
  ];

  const [polledValues, setPolledValues] = useState<Record<string, string>>({});
  const [isPolling, setIsPolling] = useState(false);

  const write =
    (options.find((x) => x.label == "write")?.value as boolean) ?? false;

  const registerTypes = write
    ? modbusRegisterWriteTypes
    : modbusRegisterAllTypes;

  const masters =
    (options.find((x) => x.label == "masters")?.value as ModbusMaster[]) ?? [];

  const sensors = (
    (options.find((x) => x.label == "sensors")?.value as SensorOptionType[]) ??
    []
  )?.map((x) => x.tagKey);

  const tableData = value
    .filter((x) => registerTypes.includes(x.type ?? 0))
    .map((x) => ({
      ...x,
      name:
        masters.find((m) => m.modbusMasterId === x.modbusMasterId)?.name ??
        x.modbusMasterId,
      polledValue: polledValues[x.modbusRegisterId ?? ""] ?? x.polledValue,
    }));

  async function handlePoll() {
    if (tableData.length === 0) return;
    setIsPolling(true);
    try {
      const result = await new ModbusApi().poll(tableData);
      if (!result.ok || !result.data) {
        toast.error(t("common.errors.general"));
        return;
      }
      const next: Record<string, string> = {};
      for (const item of result.data) {
        next[item.registerId] = item.value;
      }
      setPolledValues(next);
    } finally {
      setIsPolling(false);
    }
  }

  return (
    <div className="ml-3">
      <ModbusRegisterCreate
        registers={value}
        register={selectedRegister}
        masters={
          (
            options.find((x) => x.label == "masters")?.value as ModbusMaster[]
          ).map((x) => {
            return {
              label: x.name ?? x.modbusMasterId,
              value: x.modbusMasterId,
            };
          }) as FormOption<string>[]
        }
        sensors={sensors}
        onAdd={handleAdd}
        write={write}
      />
      {tableData.length > 0 && (
        <div className="mt-2">
          <Button
            variant="outline"
            type="button"
            disabled={isPolling}
            onClick={handlePoll}
          >
            <Label className="text-sm">{t("modbus.register.poll")}</Label>
          </Button>
        </div>
      )}
      <div className="mt-2 overflow-x-auto">
        <SimpleTable
          data={tableData}
          colDef={colDef}
          pageSizes={[10, 25]}
          onEdit={handleEdit}
          singleEditable
          onRemove={handleRemove}
          removable
        />
      </div>
    </div>
  );
}

export function ModbusRegisterCreate({
  register,
  registers = [],
  masters = [],
  sensors = [],
  onAdd,
  write = false,
}: {
  register?: ModbusRegisterDto;
  registers?: ModbusRegisterDto[];
  masters?: FormOption<string>[];
  sensors?: FormOption<string>[];
  onAdd: (val: ModbusRegisterDto) => void;
  write?: boolean;
}) {
  const { t } = useTranslation();
  const [localRegister, setLocalRegister] = useState<ModbusRegisterDto>(
    register ?? deafaultModbusRegister,
  );

  useEffect(() => {
    if (register) {
      setLocalRegister(register);
    }
  }, [register]);

  function validateFields(): { valid: boolean; message: string } {
    if (localRegister?.slave === undefined)
      return { valid: false, message: "modbus.validation.slavIDRequired" };
    if (localRegister?.slave < 0)
      return { valid: false, message: "modbus.validation.slaveMinVal" };
    if (localRegister?.slave > 255)
      return { valid: false, message: "modbus.validation.slaveMaxVal" };

    if (localRegister?.register === undefined)
      return { valid: false, message: "modbus.validation.registerRequired" };
    if (localRegister?.register < 0)
      return { valid: false, message: "modbus.validation.registerMinVal" };
    if (localRegister?.register > 66999)
      return { valid: false, message: "modbus.validation.registerMaxVal" };

    if (localRegister?.pollInterval === undefined)
      return {
        valid: false,
        message: "modbus.validation.pollIntervalRequired",
      };
    if (localRegister?.pollInterval < 60)
      return { valid: false, message: "modbus.validation.pollIntervalMin" };

    if (!canUseRegister(registers, localRegister))
      return {
        valid: false,
        message: "modbus.validation.registerAllreadyReadFrom",
      };

    return { valid: true, message: "" };
  }

  async function handleAdd() {
    if (localRegister.modbusMasterId && localRegister.key) {
      var validation = validateFields();
      if (!validation?.valid) {
        toast.error(t(validation.message));
        return;
      }

      onAdd({
        ...localRegister,
        action: write
          ? ModbusRegisterActionEnum.WRITE
          : ModbusRegisterActionEnum.POLL,
      });
      setLocalRegister(deafaultModbusRegister);
      return;
    }
    toast.error(t("common.errors.missingFields"));
  }

  const registerTypes = write
    ? modbusRegisterWriteTypes
    : modbusRegisterAllTypes;

  return (
    <ContentContainer>
      <div className="flex">
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("modbus.register.fields.modbusMasterId")}
          </Label>
          <ComboBox
            value={localRegister.modbusMasterId}
            placeholder={t("modbus.register.placeholders.modbusMasterId")}
            options={masters}
            onChange={(val) => {
              setLocalRegister({
                ...localRegister,
                modbusMasterId: val as string,
              });
            }}
          />
        </div>
        <div className="mr-2 w-1/4">
          <Label className="text-sm">{t("modbus.register.fields.slave")}</Label>
          <Input
            value={localRegister.slave ?? 1}
            type="number"
            min={0}
            max={255}
            placeholder={t("modbus.register.placeholders.slave")}
            onChange={(e) => {
              const val = e.target.valueAsNumber;
              setLocalRegister({
                ...localRegister,
                slave: Number.isNaN(val) ? undefined : val,
              });
            }}
          />
        </div>
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("modbus.register.fields.registerType")}
          </Label>
          <ComboBox
            value={localRegister.type ?? 0}
            placeholder={t("modbus.register.placeholders.registerType")}
            options={registerTypes.map((x) => {
              return {
                label: t(`modbus.register.types.${x}`),
                value: x,
              } as FormOption<number>;
            })}
            onChange={(val) => {
              setLocalRegister({
                ...localRegister,
                type: val as ModbusRegisterType,
              });
            }}
          />
        </div>
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("modbus.register.fields.register")}
          </Label>
          <Input
            value={localRegister.register ?? 1}
            type="number"
            min={0}
            max={66999}
            placeholder={t("modbus.register.placeholders.register")}
            onChange={(e) => {
              const val = e.target.valueAsNumber;
              setLocalRegister({
                ...localRegister,
                register: Number.isNaN(val) ? undefined : val,
              });
            }}
          />
        </div>
      </div>

      <div className="flex mt-1">
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("modbus.register.fields.valueType")}
          </Label>
          <ComboBox
            value={localRegister.valueType ?? 0}
            placeholder={t("modbus.register.placeholders.valueType")}
            options={modbusValueTypes.map((x) => {
              return {
                label: t(`modbus.register.valueTypes.${x}`),
                value: x,
              } as FormOption<number>;
            })}
            onChange={(val) => {
              setLocalRegister({
                ...localRegister,
                valueType: val as ModbusValueType,
              });
            }}
          />
        </div>
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("modbus.register.fields.byteOrder")}
          </Label>
          <ComboBox
            value={localRegister.byteOrder ?? 0}
            placeholder={t("modbus.register.placeholders.byteOrder")}
            options={modbusByteOrders.map((x) => {
              return {
                label: t(`modbus.register.byteOrder.${x}`),
                value: x,
              } as FormOption<number>;
            })}
            onChange={(val) => {
              setLocalRegister({
                ...localRegister,
                byteOrder: val as ModbusByteOrder,
              });
            }}
          />
        </div>
        <div className="mr-2 w-1/4">
          <Label className="text-sm">
            {t("modbus.register.fields.pollInterval")}
          </Label>
          <Input
            value={localRegister.pollInterval ?? 600}
            type="number"
            min={60}
            placeholder={t("modbus.register.placeholders.pollInterval")}
            onChange={(e) => {
              const val = e.target.valueAsNumber;
              setLocalRegister({
                ...localRegister,
                pollInterval: Number.isNaN(val) ? undefined : val,
              });
            }}
          />
        </div>
        <div className="mr-2 w-1/4">
          <Label className="text-sm">{t("modbus.register.fields.key")}</Label>
          <ComboBox
            value={localRegister.key ?? ""}
            placeholder={t("tagKey.placeholders.key")}
            options={sensors}
            onChange={(val) =>
              setLocalRegister({
                ...localRegister,
                key: val as string,
              })
            }
          />
        </div>
      </div>

      <div className="mt-1">
        <Button
          variant="outline"
          aria-label="Add Selected"
          onClick={handleAdd}
          type="button"
        >
          <Label className="text-sm">
            {localRegister.modbusRegisterId
              ? t("common.edit")
              : t("common.add")}
          </Label>
        </Button>
      </div>
    </ContentContainer>
  );
}
